import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Section from '../components/Section';
import EditableContent from '../components/EditableContent';
import ContentRenderer from '../components/ContentRenderer';
import CardGridBlock, { CardGridView, defaultCardGridData } from '../components/blocks/CardGridBlock';
import WinnersListBlock, { WinnersListView, defaultWinnersListData } from '../components/blocks/WinnersListBlock';
import NumberedStepsBlock, { NumberedStepsView, defaultNumberedStepsData } from '../components/blocks/NumberedStepsBlock';
import InfoCardsBlock, { InfoCardsView, defaultInfoCardsData } from '../components/blocks/InfoCardsBlock';
import StatCardsBlock, { StatCardsView, defaultStatCardsData } from '../components/blocks/StatCardsBlock';
import CardStepsBlock, { CardStepsView, defaultCardStepsData } from '../components/blocks/CardStepsBlock';
import CtaBannerBlock, { CtaBannerView, defaultCtaBannerData } from '../components/blocks/CtaBannerBlock';
import CompletedTracksBlock, { CompletedTracksView, defaultCompletedTracksData } from '../components/blocks/CompletedTracksBlock';
import FaqBlock, { FaqView, defaultFaqData } from '../components/blocks/FaqBlock';
import TrackPartnersBlock, { TrackPartnersView, defaultTrackPartnersData } from '../components/blocks/TrackPartnersBlock';
import SubmitCtaBlock, { SubmitCtaView, defaultSubmitCtaData } from '../components/blocks/SubmitCtaBlock';
import ChallengeTimelineBlock, { ChallengeTimelineView, defaultChallengeTimelineData } from '../components/blocks/ChallengeTimelineBlock';
import AwardsBlock, { AwardsView, defaultAwardsData } from '../components/blocks/AwardsBlock';
import {
  getPageContent,
  updateSectionPosition,
  deleteSection,
  updateCardGridSection,
  updateWinnersListSection,
  updateNumberedStepsSection,
  updateInfoCardsSection,
  updateStatCardsSection,
  updateCardStepsSection,
  updateCtaBannerSection,
  updateCompletedTracksSection,
  updateFaqSection,
  updateTrackPartnersSection,
  updateSubmitCtaSection,
  updateChallengeTimelineSection,
  updateAwardsSection,
} from '../services/contentService';
import { Row, Button, Col, Dropdown } from 'react-bootstrap';
import { PlusCircle, FileEarmarkPlus } from 'react-bootstrap-icons';
import { useAuth } from '../contexts/SecurityContext';
import { usePageCache } from '../contexts/PageCacheContext';
import LoadingSpinner from '../components/LoadingSpinner';
import config from '../config';

function DynamicPage({ pageId: propPageId }) {
  const { pageId: paramPageId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Handle nested paths by extracting the full path after /page/
  const getPageIdFromPath = () => {
    if (propPageId) return propPageId;
    if (paramPageId) return paramPageId;
    
    // For nested routes like /page/tracks/smart-charging, extract everything after /page/
    const path = location.pathname;
    if (path.startsWith('/page/')) {
      return path.substring(6); // Remove '/page/' prefix
    }
    
    return 'home';
  };
  
  const pageId = getPageIdFromPath();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextPosition, setNextPosition] = useState(1);
  const [pageExists, setPageExists] = useState(true);
  const { getCachedPage, cachePage } = usePageCache();
  const { user, userRoles, isAuthenticated } = useAuth();

  // Admin editing UI (add/edit/delete sections) is gated on the real Cognito
  // role (custom:isAdmin), resolved in SecurityContext — NOT merely on being
  // signed in. Regular authenticated users must not see editing controls.
  const isAdmin = !!(userRoles && userRoles.isAdmin);

  // Remove useCallback from fetchPageContent to prevent dependency issues
  async function fetchPageContent() {
    setLoading(true);
    
    try {
      // Check if we have cached content
      const cachedData = getCachedPage(pageId);
      if (cachedData) {
        setSections(cachedData.content);
        setPageExists(cachedData.content.length > 0);
        
        // Calculate next position
        if (cachedData.content.length > 0) {
          const maxPosition = Math.max(...cachedData.content.map(section => section.position));
          setNextPosition(maxPosition + 1);
        } else {
          setNextPosition(1);
        }
        
        setLoading(false);
        
        // Fetch fresh content in the background
        try {
          const freshContent = await getPageContent(pageId);
          cachePage(pageId, freshContent);
          setPageExists(freshContent.length > 0);
          
          // Only update if content has changed
          if (JSON.stringify(freshContent) !== JSON.stringify(cachedData.content)) {
            setSections(freshContent);
            
            if (freshContent.length > 0) {
              const maxPosition = Math.max(...freshContent.map(section => section.position));
              setNextPosition(maxPosition + 1);
            }
          }
        } catch (error) {
          console.error('Error fetching fresh content in background:', error);
          // Don't update UI state on background fetch errors
        }
        
        return;
      }
      
      // No cache, fetch from API with optimization
      
      // Use direct API call for now
      const content = await getPageContent(pageId);
      
      setSections(content);
      setPageExists(content.length > 0);
      
      // Cache the content
      cachePage(pageId, content);
      
      // Calculate the next position for a new section
      if (content.length > 0) {
        const maxPosition = Math.max(...content.map(section => section.position));
        setNextPosition(maxPosition + 1);
      } else {
        setNextPosition(1);
      }
      
    } catch (error) {
      console.error('Error fetching page content:', error);
      setSections([]);
      setPageExists(false);
    } finally {
      setLoading(false);
    }
  }

  // Use pageId directly in dependency array instead of the function
  useEffect(() => {
    
    // Set a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.warn('Content loading timeout - forcing loading to false');
      setLoading(false);
      if (sections.length === 0) {
        setPageExists(false);
      }
    }, 10000); // 10 second timeout
    
    // Create an async function inside useEffect to handle errors properly
    const loadContent = async () => {
      try {
        await fetchPageContent();
      } catch (error) {
        console.error('Error in useEffect loadContent:', error);
        setLoading(false);
        setSections([]);
        setPageExists(false);
      } finally {
        clearTimeout(timeoutId); // Clear timeout if loading completes
      }
    };
    
    loadContent();
    
    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]); // Only depend on pageId, not the function

  async function addNewSection(layout = 'single') {
    try {
      // Create the section in the database with default content
      const defaultContent = '<p>Click [edit] to modify this section...</p>';

      // Call the API to create the section using query parameters
      const url = `${config.api.url}content/section?pageId=${encodeURIComponent(pageId)}&position=${nextPosition}`;

      // Get user information for attribution
      const modifiedBy = user?.attributes?.email || user?.username || 'user';

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: defaultContent,
          contentType: 'html',
          modifiedBy: modifiedBy,
          layout,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unable to read error response');
        console.error(`HTTP Error ${response.status}:`, errorText);
        throw new Error(`Failed to create section: ${response.status} - ${errorText}`);
      }

      await response.json();

      // For a double-layout section, also seed the secondary block so it has
      // its own default content + version row.
      if (layout === 'double') {
        try {
          await fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              content: defaultContent,
              contentType: 'html',
              modifiedBy,
              layout: 'double',
              blockId: 'secondary',
            }),
          });
        } catch (seedErr) {
          console.error('Error seeding secondary block:', seedErr);
        }
      }

      // Add the new section to local state
      const newSection = {
        position: nextPosition,
        content: defaultContent,
        contentType: 'html',
        layout,
        lastUpdated: new Date().toISOString(),
        version: 1,
        ...(layout === 'double'
          ? { secondaryContent: defaultContent, secondaryVersion: 1 }
          : {}),
      };

      const newSections = [...sections, newSection];
      setSections(newSections);
      setNextPosition(nextPosition + 1);
      setPageExists(true);

      // Update cache immediately to prevent reversion
      cachePage(pageId, newSections);

    } catch (error) {
      console.error('Error creating new section:', error);
      alert('Failed to create new section. Please try again.');
    }
  }

  function createPage() {
    addNewSection('single');
  }

  // Create a new section with `blockType: 'card-grid'` and seed it with the
  // default title + two starter cards. Uses the same PUT endpoint as text
  // sections, but with a structured `data` payload instead of HTML content.
  async function addCardGridSection() {
    await _addStructuredSection('card-grid', defaultCardGridData());
  }

  async function addWinnersListSection() {
    await _addStructuredSection('winners-list', defaultWinnersListData());
  }

  async function addNumberedStepsSection() {
    await _addStructuredSection('numbered-steps', defaultNumberedStepsData());
  }

  async function addInfoCardsSection() {
    await _addStructuredSection('info-cards', defaultInfoCardsData());
  }

  async function addStatCardsSection() {
    await _addStructuredSection('stat-cards', defaultStatCardsData());
  }

  async function addCardStepsSection() {
    await _addStructuredSection('card-steps', defaultCardStepsData());
  }

  async function addCtaBannerSection() {
    await _addStructuredSection('cta-banner', defaultCtaBannerData());
  }

  async function addCompletedTracksSection() {
    await _addStructuredSection('completed-tracks', defaultCompletedTracksData());
  }

  async function addFaqSection() {
    await _addStructuredSection('faq', defaultFaqData());
  }

  async function addTrackPartnersSection() {
    await _addStructuredSection('track-partners', defaultTrackPartnersData());
  }

  async function addSubmitCtaSection() {
    await _addStructuredSection('submit-cta', defaultSubmitCtaData());
  }

  async function addChallengeTimelineSection() {
    await _addStructuredSection('challenge-timeline', defaultChallengeTimelineData());
  }

  async function addAwardsSection() {
    await _addStructuredSection('awards', defaultAwardsData());
  }

  async function _addStructuredSection(blockType, data) {
    try {
      const modifiedBy = user?.attributes?.email || user?.username || 'user';
      const saversByBlockType = {
        'card-grid': updateCardGridSection,
        'winners-list': updateWinnersListSection,
        'numbered-steps': updateNumberedStepsSection,
        'info-cards': updateInfoCardsSection,
        'stat-cards': updateStatCardsSection,
        'card-steps': updateCardStepsSection,
        'cta-banner': updateCtaBannerSection,
        'completed-tracks': updateCompletedTracksSection,
        'faq': updateFaqSection,
        'track-partners': updateTrackPartnersSection,
        'submit-cta': updateSubmitCtaSection,
        'challenge-timeline': updateChallengeTimelineSection,
        'awards': updateAwardsSection,
      };
      const save = saversByBlockType[blockType];
      await save(pageId, nextPosition, data, modifiedBy);

      const newSection = {
        position: nextPosition,
        blockType,
        data,
        lastUpdated: new Date().toISOString(),
        version: 1,
      };
      const newSections = [...sections, newSection];
      setSections(newSections);
      setNextPosition(nextPosition + 1);
      setPageExists(true);
      cachePage(pageId, newSections);
    } catch (error) {
      console.error(`Error creating ${blockType} section:`, error);
      alert(`Failed to create new ${blockType} section. Please try again.`);
    }
  }

  function handleLogin() {
    navigate('/user');
  }

  async function handleMoveSection(currentPosition, direction) {
    // Find the section to swap with
    const currentIndex = sections.findIndex(s => s.position === currentPosition);
    if (currentIndex === -1) return;
    
    const newSections = [...sections];
    
    if (direction === 'up' && currentIndex > 0) {
      // Swap with the section above
      const targetIndex = currentIndex - 1;
      
      try {
        // Update positions in the backend
        await updateSectionPosition(
          pageId, 
          newSections[currentIndex].position, 
          newSections[targetIndex].position
        );
        
        // Swap the entire section objects (not just positions)
        const currentSection = { ...newSections[currentIndex] };
        const targetSection = { ...newSections[targetIndex] };
        
        // Swap positions in the objects
        currentSection.position = newSections[targetIndex].position;
        targetSection.position = newSections[currentIndex].position;
        
        // Update the array with swapped sections
        newSections[currentIndex] = targetSection;
        newSections[targetIndex] = currentSection;
        
        // Sort sections by position to maintain correct order
        newSections.sort((a, b) => a.position - b.position);
        
        
        // Update state and cache
        setSections(newSections);
        cachePage(pageId, newSections);
        
        // Refresh content from database to ensure accuracy
        setTimeout(async () => {
          try {
            const freshContent = await getPageContent(pageId);
            setSections(freshContent);
            cachePage(pageId, freshContent);
          } catch (error) {
            console.error('Error refreshing content after swap:', error);
          }
        }, 500); // Small delay to allow database to settle
        
      } catch (error) {
        console.error('Error moving section up:', error);
        alert('Failed to move section. Please try again.');
      }
    } else if (direction === 'down' && currentIndex < sections.length - 1) {
      // Swap with the section below
      const targetIndex = currentIndex + 1;
      
      try {
        // Update positions in the backend
        await updateSectionPosition(
          pageId, 
          newSections[currentIndex].position, 
          newSections[targetIndex].position
        );
        
        // Swap the entire section objects (not just positions)
        const currentSection = { ...newSections[currentIndex] };
        const targetSection = { ...newSections[targetIndex] };
        
        // Swap positions in the objects
        currentSection.position = newSections[targetIndex].position;
        targetSection.position = newSections[currentIndex].position;
        
        // Update the array with swapped sections
        newSections[currentIndex] = targetSection;
        newSections[targetIndex] = currentSection;
        
        // Sort sections by position to maintain correct order
        newSections.sort((a, b) => a.position - b.position);
        
        
        // Update state and cache
        setSections(newSections);
        cachePage(pageId, newSections);
        
        // Refresh content from database to ensure accuracy
        setTimeout(async () => {
          try {
            const freshContent = await getPageContent(pageId);
            setSections(freshContent);
            cachePage(pageId, freshContent);
          } catch (error) {
            console.error('Error refreshing content after swap:', error);
          }
        }, 500); // Small delay to allow database to settle
        
      } catch (error) {
        console.error('Error moving section down:', error);
        alert('Failed to move section. Please try again.');
      }
    }
  }

  async function handleDeleteSection(position) {
    try {
      // Delete section in the backend
      await deleteSection(pageId, position);
      
      // Remove section locally
      const newSections = sections.filter(s => s.position !== position);
      
      // Update state and cache
      setSections(newSections);
      cachePage(pageId, newSections);
      
      // Check if page is now empty
      setPageExists(newSections.length > 0);
    } catch (error) {
      console.error('Error deleting section:', error);
      alert('Failed to delete section. Please try again.');
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading page content..." />;
  }

  if (!pageExists) {
    return (
      <Section>
        <Row className="justify-content-center">
          <Col md={8} className="text-center">
            <div className="empty-page-message">
              <h2>This page doesn't exist yet</h2>
              {isAdmin ? (
                <>
                  <p>Would you like to create it?</p>
                  <Button 
                    variant="primary" 
                    size="lg"
                    onClick={createPage}
                    className="mt-3"
                  >
                    <FileEarmarkPlus className="me-2" /> Create Page
                  </Button>
                </>
              ) : isAuthenticated ? (
                <p>This page is not available yet.</p>
              ) : (
                <>
                  <p>Please log in to create this page.</p>
                  <Button 
                    variant="primary" 
                    size="lg"
                    onClick={handleLogin}
                    className="mt-3"
                  >
                    Log In
                  </Button>
                </>
              )}
            </div>
          </Col>
        </Row>
      </Section>
    );
  }

  // Sort sections by position
  const sortedSections = [...sections].sort((a, b) => a.position - b.position);

  return (
    <>
      {sortedSections.map((section, index) => {
        const isDouble = section.layout === 'double';
        const isCardGrid = section.blockType === 'card-grid';
        const moveUp = () => handleMoveSection(section.position, 'up');
        const moveDown = () => handleMoveSection(section.position, 'down');
        const onDelete = () => handleDeleteSection(section.position);
        const firstFlag = index === 0;
        const lastFlag = index === sortedSections.length - 1;

        // Structured block types dispatch to their own renderer.
        if (isCardGrid) {
          return (
            <Section key={section.position}>
              {isAdmin ? (
                <CardGridBlock
                  pageId={pageId}
                  section={section}
                  onMoveUp={moveUp}
                  onMoveDown={moveDown}
                  onDelete={onDelete}
                  isFirstSection={firstFlag}
                  isLastSection={lastFlag}
                />
              ) : (
                <CardGridView data={section.data} />
              )}
            </Section>
          );
        }

        if (section.blockType === 'winners-list') {
          return (
            <Section key={section.position}>
              {isAdmin ? (
                <WinnersListBlock
                  pageId={pageId}
                  section={section}
                  onMoveUp={moveUp}
                  onMoveDown={moveDown}
                  onDelete={onDelete}
                  isFirstSection={firstFlag}
                  isLastSection={lastFlag}
                />
              ) : (
                <WinnersListView data={section.data} />
              )}
            </Section>
          );
        }

        if (section.blockType === 'numbered-steps') {
          return (
            <Section key={section.position}>
              {isAdmin ? (
                <NumberedStepsBlock
                  pageId={pageId}
                  section={section}
                  onMoveUp={moveUp}
                  onMoveDown={moveDown}
                  onDelete={onDelete}
                  isFirstSection={firstFlag}
                  isLastSection={lastFlag}
                />
              ) : (
                <NumberedStepsView data={section.data} />
              )}
            </Section>
          );
        }

        if (section.blockType === 'info-cards') {
          return (
            <Section key={section.position}>
              {isAdmin ? (
                <InfoCardsBlock
                  pageId={pageId}
                  section={section}
                  onMoveUp={moveUp}
                  onMoveDown={moveDown}
                  onDelete={onDelete}
                  isFirstSection={firstFlag}
                  isLastSection={lastFlag}
                />
              ) : (
                <InfoCardsView data={section.data} />
              )}
            </Section>
          );
        }

        if (section.blockType === 'stat-cards') {
          return (
            <Section key={section.position}>
              {isAdmin ? (
                <StatCardsBlock
                  pageId={pageId}
                  section={section}
                  onMoveUp={moveUp}
                  onMoveDown={moveDown}
                  onDelete={onDelete}
                  isFirstSection={firstFlag}
                  isLastSection={lastFlag}
                />
              ) : (
                <StatCardsView data={section.data} />
              )}
            </Section>
          );
        }

        if (section.blockType === 'card-steps') {
          return (
            <Section key={section.position}>
              {isAdmin ? (
                <CardStepsBlock
                  pageId={pageId}
                  section={section}
                  onMoveUp={moveUp}
                  onMoveDown={moveDown}
                  onDelete={onDelete}
                  isFirstSection={firstFlag}
                  isLastSection={lastFlag}
                />
              ) : (
                <CardStepsView data={section.data} />
              )}
            </Section>
          );
        }

        if (section.blockType === 'cta-banner') {
          return (
            <Section key={section.position}>
              {isAdmin ? (
                <CtaBannerBlock
                  pageId={pageId}
                  section={section}
                  onMoveUp={moveUp}
                  onMoveDown={moveDown}
                  onDelete={onDelete}
                  isFirstSection={firstFlag}
                  isLastSection={lastFlag}
                />
              ) : (
                <CtaBannerView data={section.data} />
              )}
            </Section>
          );
        }

        if (section.blockType === 'completed-tracks') {
          return (
            <Section key={section.position}>
              {isAdmin ? (
                <CompletedTracksBlock
                  pageId={pageId}
                  section={section}
                  onMoveUp={moveUp}
                  onMoveDown={moveDown}
                  onDelete={onDelete}
                  isFirstSection={firstFlag}
                  isLastSection={lastFlag}
                />
              ) : (
                <CompletedTracksView data={section.data} />
              )}
            </Section>
          );
        }

        if (section.blockType === 'faq') {
          return (
            <Section key={section.position}>
              {isAdmin ? (
                <FaqBlock
                  pageId={pageId}
                  section={section}
                  onMoveUp={moveUp}
                  onMoveDown={moveDown}
                  onDelete={onDelete}
                  isFirstSection={firstFlag}
                  isLastSection={lastFlag}
                />
              ) : (
                <FaqView data={section.data} />
              )}
            </Section>
          );
        }

        if (section.blockType === 'track-partners') {
          return (
            <Section key={section.position}>
              {isAdmin ? (
                <TrackPartnersBlock
                  pageId={pageId}
                  section={section}
                  onMoveUp={moveUp}
                  onMoveDown={moveDown}
                  onDelete={onDelete}
                  isFirstSection={firstFlag}
                  isLastSection={lastFlag}
                />
              ) : (
                <TrackPartnersView data={section.data} />
              )}
            </Section>
          );
        }

        if (section.blockType === 'submit-cta') {
          return (
            <Section key={section.position}>
              {isAdmin ? (
                <SubmitCtaBlock
                  pageId={pageId}
                  section={section}
                  onMoveUp={moveUp}
                  onMoveDown={moveDown}
                  onDelete={onDelete}
                  isFirstSection={firstFlag}
                  isLastSection={lastFlag}
                />
              ) : (
                <SubmitCtaView data={section.data} />
              )}
            </Section>
          );
        }

        if (section.blockType === 'challenge-timeline') {
          return (
            <Section key={section.position}>
              {isAdmin ? (
                <ChallengeTimelineBlock
                  pageId={pageId}
                  section={section}
                  onMoveUp={moveUp}
                  onMoveDown={moveDown}
                  onDelete={onDelete}
                  isFirstSection={firstFlag}
                  isLastSection={lastFlag}
                />
              ) : (
                <ChallengeTimelineView data={section.data} />
              )}
            </Section>
          );
        }

        if (section.blockType === 'awards') {
          return (
            <Section key={section.position}>
              {isAdmin ? (
                <AwardsBlock
                  pageId={pageId}
                  section={section}
                  onMoveUp={moveUp}
                  onMoveDown={moveDown}
                  onDelete={onDelete}
                  isFirstSection={firstFlag}
                  isLastSection={lastFlag}
                />
              ) : (
                <AwardsView data={section.data} />
              )}
            </Section>
          );
        }

        return (
          <Section key={section.position}>
            {isAdmin ? (
              isDouble ? (
                <Row>
                  <Col md={6}>
                    <EditableContent
                      pageId={pageId}
                      section={section}
                      blockId="primary"
                      onMoveUp={moveUp}
                      onMoveDown={moveDown}
                      onDelete={onDelete}
                      isFirstSection={firstFlag}
                      isLastSection={lastFlag}
                    />
                  </Col>
                  <Col md={6}>
                    <EditableContent
                      pageId={pageId}
                      section={section}
                      blockId="secondary"
                      showSectionControls={false}
                    />
                  </Col>
                </Row>
              ) : (
                <Row>
                  <Col>
                    <EditableContent
                      pageId={pageId}
                      section={section}
                      onMoveUp={moveUp}
                      onMoveDown={moveDown}
                      onDelete={onDelete}
                      isFirstSection={firstFlag}
                      isLastSection={lastFlag}
                    />
                  </Col>
                </Row>
              )
            ) : (
              <Row>
                <Col>
                  <ContentRenderer content={section} />
                </Col>
              </Row>
            )}
          </Section>
        );
      })}

      {isAdmin && (
        <Section>
          <Row className="justify-content-center">
            <Col className="d-flex">
              <Dropdown>
                <Dropdown.Toggle variant="outline-primary" id="add-section-dropdown" className="add-section-btn">
                  <PlusCircle /> Add Section
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => addNewSection('single')}>Rich Text (single)</Dropdown.Item>
                  <Dropdown.Item onClick={() => addNewSection('double')}>Rich Text (two-column)</Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={addInfoCardsSection}>Info Cards</Dropdown.Item>
                  <Dropdown.Item onClick={addCardGridSection}>Card Grid (active challenges)</Dropdown.Item>
                  <Dropdown.Item onClick={addStatCardsSection}>Stat Cards</Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={addNumberedStepsSection}>Numbered Steps (plain)</Dropdown.Item>
                  <Dropdown.Item onClick={addCardStepsSection}>Numbered Steps (card)</Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={addWinnersListSection}>Winners List</Dropdown.Item>
                  <Dropdown.Item onClick={addCompletedTracksSection}>Completed Tracks</Dropdown.Item>
                  <Dropdown.Item onClick={addCtaBannerSection}>CTA Banner</Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={addFaqSection}>FAQ</Dropdown.Item>
                  <Dropdown.Item onClick={addTrackPartnersSection}>Track Partners</Dropdown.Item>
                  <Dropdown.Item onClick={addSubmitCtaSection}>Submit-Solution CTA</Dropdown.Item>
                  <Dropdown.Item onClick={addChallengeTimelineSection}>Challenge Timeline</Dropdown.Item>
                  <Dropdown.Item onClick={addAwardsSection}>Awards</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>
        </Section>
      )}
    </>
  );
}

export default DynamicPage;
